/**
 * `[[...]]` blank markers, shared by FILL_THE_LETTERS and DRAG_WORDS.
 *
 * Both exercise types are authored inline: the author types the answer right
 * into the Text or Question, wrapped in double square brackets. What the
 * brackets mean depends on the type:
 *  - FILL_THE_LETTERS: `c[[at]]` — the bracketed letters are missing and the
 *    candidate types each one into its own box.
 *  - DRAG_WORDS: `the [[cat]] sat` — the bracketed word becomes an empty slot
 *    and a card for it joins the word bank.
 *
 * Markers are only recognized in text content, never inside a tag or an
 * attribute, and a marker cannot span formatting (`[[a<b>t</b>]]` stays as
 * typed). Whatever is not recognized is shown to the candidate as plain text,
 * so the editor can point it out.
 *
 * Shared with the admin editor (sensay-assessment-admin:
 * src/utils/bracketBlanks.js), which scans with the exact same rules so what
 * it reports about a definition matches what the candidate actually sees.
 * Keep the two copies in sync.
 */

// Source only — callers build their own RegExp so the shared `lastIndex` of a
// global literal can never leak between scans.
export const BLANK_MARKER_SOURCE = "\\[\\[([^\\[\\]]*)\\]\\]";

/**
 * Splits HTML into tag chunks and text chunks, in order. A `<` that does not
 * open a tag (`a < b`) stays text; quoted attribute values may contain `>`.
 */
export function splitHtmlChunks(html) {
  const source = String(html ?? "");
  const chunks = [];
  let textStart = 0;

  for (let i = 0; i < source.length; i++) {
    if (source[i] !== "<" || !/[A-Za-z!/?]/.test(source[i + 1] || "")) continue;

    let quote = null;
    let tagEnd = -1;
    if (source.startsWith("<!--", i)) {
      const commentEnd = source.indexOf("-->", i + 4);
      if (commentEnd !== -1) tagEnd = commentEnd + 2;
    } else {
      for (let j = i + 1; j < source.length; j++) {
        const char = source[j];
        if (quote) {
          if (char === quote) quote = null;
        } else if (char === '"' || char === "'") {
          quote = char;
        } else if (char === ">") {
          tagEnd = j;
          break;
        }
      }
    }
    if (tagEnd === -1) continue;

    if (i > textStart) chunks.push({ tag: false, value: source.slice(textStart, i) });
    chunks.push({ tag: true, value: source.slice(i, tagEnd + 1) });
    textStart = tagEnd + 1;
    i = tagEnd;
  }

  if (textStart < source.length) {
    chunks.push({ tag: false, value: source.slice(textStart) });
  }
  return chunks;
}

const NAMED_ENTITIES = {
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  apos: "'",
  nbsp: " ",
};

/** Decodes the entities a rich editor emits in text content. */
export function decodeHtmlEntities(value) {
  return String(value ?? "").replace(
    /&(#x[\da-f]+|#\d+|[a-z]+);/gi,
    (entity, body) => {
      if (body[0] === "#") {
        const code =
          body[1] === "x" || body[1] === "X"
            ? Number.parseInt(body.slice(2), 16)
            : Number.parseInt(body.slice(1), 10);
        return Number.isFinite(code) && code > 0 && code <= 0x10ffff
          ? String.fromCodePoint(code)
          : entity;
      }
      const named = NAMED_ENTITIES[body.toLowerCase()];
      return named === undefined ? entity : named;
    },
  );
}

export function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/**
 * Splits one authored field at its `[[...]]` markers.
 *
 * `accept(content)` gets the decoded marker content and returns the blank it
 * stands for, or null to leave that marker as literal text (an empty one, for
 * example). It is called in document order, so callers can number blanks as
 * they go.
 *
 * Returns parts in document order: `{ html }` for authored markup, untouched,
 * and `{ blank }` for every accepted marker. Joining the `html` parts with a
 * rendering of each blank gives the field as the candidate sees it.
 */
export function splitOnBlankMarkers(html, accept) {
  const parts = [];
  let buffer = "";

  for (const chunk of splitHtmlChunks(html)) {
    if (chunk.tag) {
      buffer += chunk.value;
      continue;
    }
    const regex = new RegExp(BLANK_MARKER_SOURCE, "g");
    let consumed = 0;
    let match;
    while ((match = regex.exec(chunk.value)) !== null) {
      const blank = accept(decodeHtmlEntities(match[1]));
      if (blank === null || blank === undefined) continue;
      buffer += chunk.value.slice(consumed, match.index);
      parts.push({ html: buffer });
      parts.push({ blank });
      buffer = "";
      consumed = match.index + match[0].length;
    }
    buffer += chunk.value.slice(consumed);
  }

  if (buffer) parts.push({ html: buffer });
  return parts;
}

/**
 * How many `[[` / `]]` are left in the text the candidate reads — markers
 * that were empty, unclosed, or split by formatting, so they show as typed.
 */
export function countStrayMarkers(parts = []) {
  let count = 0;
  for (const part of parts || []) {
    if (part?.html === undefined) continue;
    for (const chunk of splitHtmlChunks(part.html)) {
      if (chunk.tag) continue;
      count += (chunk.value.match(/\[\[|\]\]/g) || []).length;
    }
  }
  return count;
}

/** Renders parsed parts back to HTML, with `renderBlank` for each blank. */
export function joinBlankParts(parts = [], renderBlank) {
  return (parts || [])
    .map((part) => (part?.blank !== undefined ? renderBlank(part.blank) : part?.html ?? ""))
    .join("");
}

// A run of word characters (or punctuation) at the end / start of authored
// markup, never reaching into a tag.
const TRAILING_WORD = /[^\s<>]+$/;
const LEADING_WORD = /^[^\s<>]+/;

/**
 * Like joinBlankParts, but each blank stays on one line with the word it
 * sits in: `c[[at]]`, `m[[a]]t`, `([[cat]]).` are wrapped together in a
 * `<span class="blank-word">` the components style `white-space: nowrap`.
 * Browsers may otherwise break a line between a letter and the box next to it.
 * Only text touching the blank is pulled in, so markup is never re-nested.
 */
export function joinBlankPartsKeepingWords(parts = [], renderBlank) {
  let out = "";
  let open = false;
  const list = parts || [];

  list.forEach((part, i) => {
    if (part?.blank !== undefined) {
      if (!open) {
        const prefix = (out.match(TRAILING_WORD) || [""])[0];
        out = `${out.slice(0, out.length - prefix.length)}<span class="blank-word">${prefix}`;
        open = true;
      }
      out += renderBlank(part.blank);
      return;
    }
    let html = part?.html ?? "";
    if (open) {
      const suffix = (html.match(LEADING_WORD) || [""])[0];
      out += suffix;
      html = html.slice(suffix.length);
      // `m[[a]]t[[e]]r`: the letters between two blanks belong to one word.
      if (html === "" && list[i + 1]?.blank !== undefined) return;
      out += "</span>";
      open = false;
    }
    out += html;
  });

  return open ? `${out}</span>` : out;
}

// Apostrophe look-alikes the platform's sanitizer folds into "'" in the text,
// folded the same way in typed answers and extra words so they still match.
const APOSTROPHE_LIKE = /[’‘ʼʻ`´]/g;

/** Comparable form of an answer: NFC, plain apostrophes, single spaces. */
export function normalizeBlankText(value) {
  return String(value ?? "")
    .normalize("NFC")
    .replace(APOSTROPHE_LIKE, "'")
    .replace(/\s+/g, " ")
    .trim();
}

/** The fields the markers are read from, in order: question first, as on FILL_THE_GAP. */
export const BLANK_FIELDS = ["question", "text"];
